// Options that live in this browser only (sound, music, animations). Nothing here is sent to the server.
import { useEffect, useState } from 'react';

export type Motion = 'full' | 'reduced' | 'off';

export interface Settings {
  /** Overall volume, 0..1. */
  master: number;
  /** The speaker button in the top bar: silences everything without losing the other choices. */
  muted: boolean;
  sfx: boolean;
  sfxVolume: number;
  music: boolean;
  musicVolume: number;
  /** Soft click on buttons and tabs. */
  clicks: boolean;
  /** Sounds for chat messages, @mentions and things that happen to you. */
  alerts: boolean;
  /** Desktop pop-ups while the game is open in a background tab. */
  desktopAlerts: boolean;
  /** Animations: full, reduced (no shaking or flashing, quicker) or off. */
  motion: Motion;
  /** Count cash and XP up instead of jumping straight to the new number. */
  countUp: boolean;
}

const KEY = 'mm-settings';

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export const DEFAULTS: Settings = {
  master: 0.8,
  muted: false,
  sfx: true,
  sfxVolume: 0.8,
  music: true,
  musicVolume: 0.35,
  clicks: true,
  alerts: true,
  desktopAlerts: false,
  motion: 'full',
  countUp: true,
};

function load(): Settings {
  const base: Settings = { ...DEFAULTS, motion: prefersReducedMotion() ? 'reduced' : 'full' };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return base;
    const saved = JSON.parse(raw);
    return { ...base, ...(saved && typeof saved === 'object' ? saved : {}) };
  } catch {
    return base;
  }
}

let current: Settings = load();
const listeners = new Set<(s: Settings) => void>();

export function getSettings(): Settings {
  return current;
}

export function updateSettings(patch: Partial<Settings>) {
  current = { ...current, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch { /* private mode: settings last until the page closes */ }
  applyMotionClass();
  for (const l of listeners) l(current);
}

export function resetSettings() {
  try {
    localStorage.removeItem(KEY);
  } catch { /* ignore */ }
  current = load();
  applyMotionClass();
  for (const l of listeners) l(current);
}

export function onSettings(fn: (s: Settings) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** React hook: re-renders when any option changes. */
export function useSettings(): Settings {
  const [s, set] = useState(current);
  useEffect(() => onSettings(set), []);
  return s;
}

/** CSS hooks: `motion-reduced` / `motion-off` on <html> tone animations down. */
export function applyMotionClass() {
  const el = document.documentElement;
  el.classList.toggle('motion-reduced', current.motion === 'reduced');
  el.classList.toggle('motion-off', current.motion === 'off');
}

export const motionOn = () => current.motion !== 'off';
export const motionFull = () => current.motion === 'full';
